import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  Divider, 
  CircularProgress,
  Chip,
  useTheme,
  Paper,
  Grid
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon, 
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
  Flag as FlagIcon,
  Today as TodayIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicDisplay } from '../../components/PublicDisplayWrapper';
import { format, parseISO, isAfter, isBefore, addDays, isSameDay, isToday, isPast, isFuture } from 'date-fns';
import config from '../../config';

function TasksDisplay({ isPublic = false, hardcodedToken = null }) {
  const { token } = useAuth();
  // Always call the hook, but only use its value if isPublic is true
  const publicDisplayContext = usePublicDisplay();
  // Use hardcoded token if provided, otherwise use the context token
  const publicToken = isPublic ? (hardcodedToken || publicDisplayContext.publicToken) : null;
  const theme = useTheme();
  
  // State for tasks data
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Current time for the clock
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update the time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);

  // Fetch tasks from API
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      
      // Use different endpoints based on whether this is public or authenticated
      let endpoint, headers;
      
      if (isPublic && publicToken) {
        endpoint = `${config.apiUrl}/public/tasks?token=${publicToken}`;
        headers = { 'Content-Type': 'application/json' };
        console.log('Using public endpoint for tasks:', endpoint);
      } else {
        endpoint = `${config.apiUrl}/tasks`;
        headers = { 'Authorization': `Bearer ${token}` };
        console.log('Using authenticated endpoint for tasks:', endpoint);
      }
      
      const response = await fetch(endpoint, { headers });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API error:', response.status, errorData);
        throw new Error(`API error: ${response.status} ${errorData.error || 'Unknown error'}`);
      }
      
      const data = await response.json();
      console.log('Raw tasks data from API:', data);
      
      // Sort by due date and priority
      const sortedTasks = data.sort((a, b) => {
        // First sort by completion status
        if (a.completed !== b.completed) {
          return a.completed ? 1 : -1;
        }
        
        // Then sort by due date
        if (a.dueDate && b.dueDate) {
          return new Date(a.dueDate) - new Date(b.dueDate);
        }
        
        // If only one has a due date, prioritize it
        if (a.dueDate) return -1;
        if (b.dueDate) return 1;
        
        // Finally sort by priority
        return (b.priority || 0) - (a.priority || 0);
      });
      
      setTasks(sortedTasks);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setError(error.message || 'Failed to load tasks');
      setTasks([]); // Set empty array to prevent processing errors
      setLoading(false);
    }
  }, [token, isPublic, publicToken]);

  useEffect(() => {
    // Fetch data
    fetchTasks();
    
    // Set up refresh interval (every 5 minutes)
    const refreshInterval = setInterval(() => {
      fetchTasks();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [fetchTasks]);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(parseISO(dateString), 'EEEE, MMMM d, yyyy');
  };

  // Group tasks by due date category
  const groupTasksByDueDate = (tasks) => {
    const today = new Date();
    const tomorrow = addDays(today, 1);
    const nextWeek = addDays(today, 7);
    
    const overdue = [];
    const dueToday = [];
    const dueTomorrow = [];
    const dueThisWeek = [];
    const noDueDate = [];
    
    tasks.forEach(task => {
      // Skip completed or archived tasks for display
      if (task.status === 'completed' || task.completed || task.status === 'archived' || task.archived) {
        return;
      }
      
      if (!task.dueDate) {
        noDueDate.push(task);
        return;
      }
      
      const dueDate = parseISO(task.dueDate);
      
      if (isPast(dueDate) && !isToday(dueDate)) {
        overdue.push(task);
      } else if (isToday(dueDate)) {
        dueToday.push(task);
      } else if (isSameDay(dueDate, tomorrow)) {
        dueTomorrow.push(task);
      } else if (isBefore(dueDate, nextWeek)) {
        dueThisWeek.push(task);
      } else {
        // Add future tasks to dueThisWeek instead of a separate category
        dueThisWeek.push(task);
      }
    });
    
    // Sort dueThisWeek by due date (ascending)
    dueThisWeek.sort((a, b) => {
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return parseISO(a.dueDate) - parseISO(b.dueDate);
    });
    
    return { overdue, dueToday, dueTomorrow, dueThisWeek, noDueDate };
  };

  const groupedTasks = groupTasksByDueDate(tasks);

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return theme.palette.error.main;
      case 'medium':
        return theme.palette.warning.main;
      case 'low':
        return theme.palette.success.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  // Get priority label
  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high':
        return 'High';
      case 'medium':
        return 'Medium';
      case 'low':
        return 'Low';
      default:
        return 'Normal';
    }
  };

  // Render task group
  const renderTaskGroup = (title, tasks, icon, color) => {
    if (tasks.length === 0) return null;
    
    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 2,
          borderBottom: `2px solid ${color}`,
          pb: 1
        }}>
          {icon}
          <Typography variant="h5" sx={{ ml: 1, color }}>
            {title} ({tasks.length})
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          {tasks.map((task) => {
            // Calculate progress if there's a checklist
            const hasChecklist = task.checklist && task.checklist.length > 0;
            const completedItems = hasChecklist 
              ? task.checklist.filter(item => item.completed).length 
              : 0;
            const totalItems = hasChecklist ? task.checklist.length : 0;
            const progress = hasChecklist && totalItems > 0 
              ? Math.round((completedItems / totalItems) * 100) 
              : 0;
              
            return (
              <Grid item xs={12} md={6} lg={4} key={task._id}>
                <Card 
                  elevation={3} 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderTop: `4px solid ${task.backgroundColor || color}`,
                    bgcolor: task.status === 'completed' ? '#f5f5f5' : 'white'
                  }}
                >
                  <CardHeader
                    title={
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                          color: task.status === 'completed' ? 'text.secondary' : 'text.primary',
                          fontWeight: task.status === 'completed' ? 'normal' : 'medium',
                        }}
                      >
                        {task.title}
                      </Typography>
                    }
                    action={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {task.priority && (
                          <Chip 
                            icon={<FlagIcon />}
                            label={getPriorityLabel(task.priority)}
                            size="small"
                            sx={{ 
                              bgcolor: getPriorityColor(task.priority),
                              color: 'white',
                              fontWeight: 'bold',
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                  <CardContent sx={{ flexGrow: 1, pt: 0 }}>
                    {task.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {task.description}
                      </Typography>
                    )}
                    
                    {/* Due date */}
                    {task.dueDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Due: {formatDate(task.dueDate)}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Progress bar */}
                    {hasChecklist && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            Progress
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {completedItems}/{totalItems} ({progress}%)
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: '100%', 
                          height: 8, 
                          bgcolor: '#e0e0e0', 
                          borderRadius: 5,
                          overflow: 'hidden'
                        }}>
                          <Box sx={{ 
                            width: `${progress}%`, 
                            height: '100%', 
                            bgcolor: progress === 100 ? 'success.main' : 'primary.main',
                            transition: 'width 0.5s ease-in-out'
                          }} />
                        </Box>
                      </Box>
                    )}
                    
                    {/* Checklist */}
                    {hasChecklist && (
                      <List dense sx={{ mt: 1, p: 0 }}>
                        {task.checklist.map((item, index) => (
                          <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              {item.completed ? (
                                <CheckCircleIcon color="success" fontSize="small" />
                              ) : (
                                <RadioButtonUncheckedIcon color="action" fontSize="small" />
                              )}
                            </ListItemIcon>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                textDecoration: item.completed ? 'line-through' : 'none',
                                color: item.completed ? 'text.secondary' : 'text.primary'
                              }}
                            >
                              {item.text}
                            </Typography>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      flexGrow: 1, 
      p: 4, 
      height: '100vh', 
      overflow: 'hidden',
      bgcolor: '#f5f5f5',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header with time and date */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 2, 
          mb: 3, 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#f57c00', // Orange background
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AssignmentIcon sx={{ mr: 1, color: 'white' }} />
          <Typography variant="h4">
            Team Tasks
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="subtitle1">
            {format(currentTime, 'EEEE, MMMM d, yyyy')}
          </Typography>
        </Box>
      </Paper>
      
      {/* Main content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={60} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error" variant="h5">{error}</Typography>
          </Box>
        ) : tasks.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" color="textSecondary">
              No tasks found.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowY: 'auto' }}>
            {renderTaskGroup(
              'Overdue', 
              groupedTasks.overdue, 
              <WarningIcon sx={{ color: theme.palette.error.main }} />, 
              theme.palette.error.main
            )}
            
            {renderTaskGroup(
              'Due Today', 
              groupedTasks.dueToday, 
              <TodayIcon sx={{ color: theme.palette.warning.main }} />, 
              theme.palette.warning.main
            )}
            
            {renderTaskGroup(
              'Due Tomorrow', 
              groupedTasks.dueTomorrow, 
              <CalendarTodayIcon sx={{ color: theme.palette.info.main }} />, 
              theme.palette.info.main
            )}
            
            {/* Render upcoming tasks without a header */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {groupedTasks.dueThisWeek.map((task) => {
                // Calculate progress if there's a checklist
                const hasChecklist = task.checklist && task.checklist.length > 0;
                const completedItems = hasChecklist 
                  ? task.checklist.filter(item => item.completed).length 
                  : 0;
                const totalItems = hasChecklist ? task.checklist.length : 0;
                const progress = hasChecklist && totalItems > 0 
                  ? Math.round((completedItems / totalItems) * 100) 
                  : 0;
                  
                return (
                  <Grid item xs={12} md={6} lg={4} key={task._id}>
                    <Card 
                      elevation={3} 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        borderTop: `4px solid ${task.backgroundColor || theme.palette.primary.main}`,
                        bgcolor: task.status === 'completed' ? '#f5f5f5' : 'white'
                      }}
                    >
                      <CardHeader
                        title={
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                              color: task.status === 'completed' ? 'text.secondary' : 'text.primary',
                              fontWeight: task.status === 'completed' ? 'normal' : 'medium',
                            }}
                          >
                            {task.title}
                          </Typography>
                        }
                        action={
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {task.priority && (
                              <Chip 
                                icon={<FlagIcon />}
                                label={getPriorityLabel(task.priority)}
                                size="small"
                                sx={{ 
                                  bgcolor: getPriorityColor(task.priority),
                                  color: 'white',
                                  fontWeight: 'bold',
                                }}
                              />
                            )}
                          </Box>
                        }
                      />
                      <CardContent sx={{ flexGrow: 1, pt: 0 }}>
                        {task.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {task.description}
                          </Typography>
                        )}
                        
                        {/* Due date */}
                        {task.dueDate && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              Due: {formatDate(task.dueDate)}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Progress bar */}
                        {hasChecklist && (
                          <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                Progress
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {completedItems}/{totalItems} ({progress}%)
                              </Typography>
                            </Box>
                            <Box sx={{ 
                              width: '100%', 
                              height: 8, 
                              bgcolor: '#e0e0e0', 
                              borderRadius: 5,
                              overflow: 'hidden'
                            }}>
                              <Box sx={{ 
                                width: `${progress}%`, 
                                height: '100%', 
                                bgcolor: progress === 100 ? 'success.main' : 'primary.main',
                                transition: 'width 0.5s ease-in-out'
                              }} />
                            </Box>
                          </Box>
                        )}
                        
                        {/* Checklist */}
                        {hasChecklist && (
                          <List dense sx={{ mt: 1, p: 0 }}>
                            {task.checklist.map((item, index) => (
                              <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  {item.completed ? (
                                    <CheckCircleIcon color="success" fontSize="small" />
                                  ) : (
                                    <RadioButtonUncheckedIcon color="action" fontSize="small" />
                                  )}
                                </ListItemIcon>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    textDecoration: item.completed ? 'line-through' : 'none',
                                    color: item.completed ? 'text.secondary' : 'text.primary'
                                  }}
                                >
                                  {item.text}
                                </Typography>
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
            
            {/* Render tasks without due date without a header */}
            <Grid container spacing={2}>
              {groupedTasks.noDueDate.map((task) => {
                // Calculate progress if there's a checklist
                const hasChecklist = task.checklist && task.checklist.length > 0;
                const completedItems = hasChecklist 
                  ? task.checklist.filter(item => item.completed).length 
                  : 0;
                const totalItems = hasChecklist ? task.checklist.length : 0;
                const progress = hasChecklist && totalItems > 0 
                  ? Math.round((completedItems / totalItems) * 100) 
                  : 0;
                  
                return (
                  <Grid item xs={12} md={6} lg={4} key={task._id}>
                    <Card 
                      elevation={3} 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        borderTop: `4px solid ${task.backgroundColor || theme.palette.text.secondary}`,
                        bgcolor: task.status === 'completed' ? '#f5f5f5' : 'white'
                      }}
                    >
                      <CardHeader
                        title={
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                              color: task.status === 'completed' ? 'text.secondary' : 'text.primary',
                              fontWeight: task.status === 'completed' ? 'normal' : 'medium',
                            }}
                          >
                            {task.title}
                          </Typography>
                        }
                        action={
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {task.priority && (
                              <Chip 
                                icon={<FlagIcon />}
                                label={getPriorityLabel(task.priority)}
                                size="small"
                                sx={{ 
                                  bgcolor: getPriorityColor(task.priority),
                                  color: 'white',
                                  fontWeight: 'bold',
                                }}
                              />
                            )}
                          </Box>
                        }
                      />
                      <CardContent sx={{ flexGrow: 1, pt: 0 }}>
                        {task.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {task.description}
                          </Typography>
                        )}
                        
                        {/* Progress bar */}
                        {hasChecklist && (
                          <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                Progress
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {completedItems}/{totalItems} ({progress}%)
                              </Typography>
                            </Box>
                            <Box sx={{ 
                              width: '100%', 
                              height: 8, 
                              bgcolor: '#e0e0e0', 
                              borderRadius: 5,
                              overflow: 'hidden'
                            }}>
                              <Box sx={{ 
                                width: `${progress}%`, 
                                height: '100%', 
                                bgcolor: progress === 100 ? 'success.main' : 'primary.main',
                                transition: 'width 0.5s ease-in-out'
                              }} />
                            </Box>
                          </Box>
                        )}
                        
                        {/* Checklist */}
                        {hasChecklist && (
                          <List dense sx={{ mt: 1, p: 0 }}>
                            {task.checklist.map((item, index) => (
                              <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  {item.completed ? (
                                    <CheckCircleIcon color="success" fontSize="small" />
                                  ) : (
                                    <RadioButtonUncheckedIcon color="action" fontSize="small" />
                                  )}
                                </ListItemIcon>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    textDecoration: item.completed ? 'line-through' : 'none',
                                    color: item.completed ? 'text.secondary' : 'text.primary'
                                  }}
                                >
                                  {item.text}
                                </Typography>
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default TasksDisplay; 